import { defaultsDeep } from "lodash"
import loglevel from "loglevel"
import { v4 as uuidV4 } from "uuid"
import { isAndroid, isIOS, isSafari } from "../browser"
import { EventEmitterType, eventEmitter } from "../utils/events"
import { AesCBCDecryptV3 } from "../utils/secureUtil"
import { normalizeAppName } from '../utils/appName'
import { getCookie } from '../utils/cookie'
import { EUFY_HOME_APPLICATION_NAME, EUFY_LIFT_APPLICATION_NAME } from '../utils/constants'

export const EVT_PAGE_VISITED = 10001
export const EVT_PAGE_LOADING = 10002
export const EVT_PAGE_STAYING = 10003
export const EVT_ITEM_EXPOSED = 10004
export const EVT_CLICK = 10100

const isEnvDevelopment = process.env.NODE_ENV !== "production"

const SESSION_ID = uuidV4().substr(-4) + `_${Date.now()}`

const logger = loglevel.getLogger("JSBridge")

const LIST_PROD = ["pr", "eu", "us"]

const EVT_FN_NOT_EXISTS = "EVT_FN_NOT_EXISTS"
const EVT_TIMEOUT = "EVT_TIMEOUT"

// 判断范围宽松些
const TIMEOUT = 500

function ready(fn) {
  if (
    document.readyState === "complete" ||
    document.readyState === "interactive"
  ) {
    setTimeout(fn, 1)
  } else {
    document.addEventListener("DOMContentLoaded", fn, false)
  }
}

if (typeof window !== "undefined") {
  ready(() => {
    /** 供 App 调用 */
    const eufyBridge = {
      /**
       * 切换暗黑模式通知
       * @param  {[type]} type [0: 正常模式  1： 暗黑模式]
       */
      toggleTheme(type) {
        getAppName().then((appName) => {
          const app = appName.replace(/\s/g, "-").toLowerCase()
          if (["eufy-care", "eufy-baby"].includes(app)) {
            document.body.classList.toggle("dark-theme", type === 2)
          } else {
            document.body.classList.toggle("dark-theme", type + "" === "1")
          }
          eventEmitter.emit(EventEmitterType.toggleTheme, type)
          console.log(`eufyBridge.toggleTheme: ${type}`)
        })
        // TODO: 增加事件通知
      },
    }

    window.eufyBridge = eufyBridge
  })
}

// 非 production 环境并且非 pr 环境, 则按默认展示
if (
  process.env.NODE_ENV === "production" &&
  LIST_PROD.includes(process.env.REACT_APP_ENV)
) {
  logger.setLevel("error")
} else {
  logger.enableAll()
}

function assembleAbsoluteUrl(url) {
  let absoluteUrl = url
  if (/^([a-zA-Z]+):\/\//.test(url)) {
    return absoluteUrl
  }
  // 判断绝对路径还是相对路径
  if (/^https?:\/\//.test(url)) {
    // discard
  } else {
    const location = window.location
    const hostname = location.hostname
    const port = location.port ? `:${location.port}` : ""
    const protocol = location.protocol
    absoluteUrl = [
      `${protocol}//${hostname}${port}`,
      `${url}`.replace(/\/+/g, "/"),
    ].join("")
  }
  return absoluteUrl
}

function setupWebViewJavascriptBridge(callback) {
  // 目前 iOS App 使用到的方案
  if (window.top.WebViewJavascriptBridge) {
    return callback(window.top.WebViewJavascriptBridge)
  }
  // FIXME: 待确定哪里用到, 如果没用到则可以删除
  if (window.top.WVJBCallbacks) {
    return window.top.WVJBCallbacks.push(callback)
  }
  window.top.WVJBCallbacks = [callback]
  const docEl = document.documentElement
  const WVJBIframe = document.createElement("iframe")
  WVJBIframe.style.display = "none"
  WVJBIframe.src = "wvjbscheme://__bridge_loaded__"
  docEl.appendChild(WVJBIframe)
  setTimeout(() => {
    docEl.removeChild(WVJBIframe)
  })
}

/**
 *
 * @param {string} cmd 方法名
 * @param {...any} params iOS 只能传入一个参数, Android 传入参数根据实际接口要求
 * @returns
 */
function runCommand(cmd, ...params) {
  if (isAndroid) {
    const fn = window.EufyJS?.[cmd]
    if (typeof fn === "function") {
      // 方法不能解构使用, 否则会报 injected 错误, 需要通过对象方法去调用
      return Promise.resolve(window.EufyJS[cmd](...params))
    }
    return Promise.reject({
      code: EVT_FN_NOT_EXISTS,
      message: `API: ${cmd} not found.`,
    })
  }
  if (isIOS) {
    return new Promise((resolve, reject) => {
      setTimeout(
        reject.bind(null, {
          code: EVT_FN_NOT_EXISTS,
          message: `API: ${cmd} not found.`,
        }),
        TIMEOUT
      )
      setupWebViewJavascriptBridge((bridge) => {
        bridge.callHandler(cmd, ...params, (response) => {
          resolve(response)
        })
      })
    })
  }
}

async function getProtocolPrefix() {
  let appName = await getAppName()
  return appName.replace(/\s/g, "").toLowerCase()
}

export function track(params) {
  logger.info(`API track called with: ${JSON.stringify(params)}`)
  if (isIOS) {
    return runCommand("logEventWithParam", { ...params, key_event: true })
  }
  if (isAndroid) {
    return runCommand("collectData", JSON.stringify(params))
  }
}

export function setNavbar(config) {
  logger.info(`API setNavbar called with: ${JSON.stringify(config)}`)
  if (isAndroid) {
    return runCommand("navBarStatusChange", JSON.stringify(config))
  }
  return runCommand("navBarStatusChange", config)
}

export function setNavbarAsync(config) {
  logger.info(`API setNavbarAsync called with: ${JSON.stringify(config)}`)
  return Promise.resolve(
    setNavbar(config)
      .then((response) => {
        if (isIOS) {
          return response
        }

        if (isAndroid) {
          return JSON.parse(response)
        }
      })
      .catch((err) => {
        console.log(err)
      })
  )
}

export async function isInApp() {
  if (typeof window === "undefined") {
    return Promise.resolve({})
  }
  if (isAndroid) {
    const customWindow = window
    return new Promise((resolve, reject) => {
      return resolve(
        !!customWindow.EufyJS || !!customWindow.top.WebViewJavascriptBridge
      )
    })
  }
  if (isIOS) {
    return Promise.resolve(!isSafari)
  }
  return Promise.resolve(false)
}

// 根据app类型处理返回的native信息，保证结果和字段名一致
const normalizeNativeInfo = res => {
  const appName = normalizeAppName(res.appName)
  if ([EUFY_HOME_APPLICATION_NAME, EUFY_LIFT_APPLICATION_NAME].includes(appName)) {
    return {
      ...res,
      token: res.ucToken,
      accessToken: res.token,
    }
  }
  return res
}

export async function getNativeInfo() {
  if (await isInApp()) {
    if (isIOS) {
      return new Promise((resolve, reject) => {
        setTimeout(
          reject.bind(null, { message: "Get native info timeout." }),
          TIMEOUT
        )
        setupWebViewJavascriptBridge((bridge) => {
          bridge.callHandler("getNativeInfo", {}, (response) => {
            resolve(normalizeNativeInfo(response))
          })
        })
      })
    }
    if (isAndroid) {
      return Promise.resolve(runCommand("getNativeInfo")).then((resp) =>
        normalizeNativeInfo(JSON.parse(resp))
      
      ).catch((err) => {
        return {}
      })
    }
  }
  return Promise.resolve({})
}

export function uploadHelpEvent(key, helpful, level) {
  logger.info(`API uplaodHelpEvent called.`)
  if (isIOS) {
    return runCommand("logFAQEvent", {
      key,
      level: String(level),
      helpful: String(helpful),
    })
  }
  if (isAndroid) {
    // Android 传入为多个参数
    return runCommand("reportFaqEvent", key, String(level), String(helpful))
  }
  return Promise.reject(`Unknown platform: ${window.navigator.userAgent}`)
}

/**
 * 返回上一个 App 页面, ***不要在 Explore 首页调用***
 * @returns
 */
export function backToNative() {
  logger.info("API backTonative called.")
  return runCommand("backToNative")
}

let appName

/** 避免反复调用, 并且处理兼容问题 */
export async function getAppName() {
  if (typeof appName === "undefined") {
    // 与运营平台上的数据对齐
    const REACT_APP_NAME = (process.env.REACT_APP_NAME || "")
      .replace(/^(eufy)(.+)$/, "$1 $2")
      .toLowerCase()
    try {
      const nativeInfo = await getNativeInfo()
      console.log("get app native info", nativeInfo)
      // 由于ios改了appName为eufy baby,暂时兼容一下传eufy care
      if (nativeInfo.appName === "eufy Baby") {
        nativeInfo.appName = "eufy care"
      }
      if(nativeInfo.appName?.toLowerCase()?.includes('mega')) {
        nativeInfo.appName = 'eufy_mega'
      }
      appName = nativeInfo.appName || REACT_APP_NAME
    } catch (err) {
      console.log("get app native info err", err)
      appName = REACT_APP_NAME
    }
  }
  return appName || "eufy security"
}

/**
 * 上报埋点数据
 * @param params
 * @returns
 */
export function uploadTrackings(params) {
  const { events } = params

  const date = new Date()
  const year = date.getFullYear()
  const months = `0${date.getMonth() + 1}`.substr(-2)
  const days = `0${date.getDate()}`.substr(-2)
  const hours = `0${date.getHours()}`.substr(-2)
  const minutes = `0${date.getMinutes()}`.substr(-2)
  const seconds = `0${date.getSeconds()}`.substr(-2)

  events.forEach((v) => {
    defaultsDeep(v, {
      device_sn: "",
      device_model: "",
      station_sn: "",
      station_model: "",
      extra: "", // 其他参数
      session_id: SESSION_ID, // 供大数据区分
      local_time: `${year}-${months}-${days} ${hours}:${minutes}:${seconds}`, // 本地时间(yyyy-MM-dd HH:mm:ss)
      ts: parseInt((Date.now()).toString()), // 时间戳(毫秒)
    })
  })
  console.log("uploadTrackings data: ", params)
  if (isAndroid) {
    // 注意, android 接受 json 字符串参数
    return runCommand("uploadTrackings", JSON.stringify(params))
  }
  return runCommand("uploadTrackings", params)
}

export function openWebview(url) {
  const iframe = document.createElement("iframe")
  let absoluteUrl = assembleAbsoluteUrl(url)
  iframe.style.display = "none"

  getProtocolPrefix().then(async (prefix) => {
    let appName = getCookie('app_name')

    // 兜底逻辑, 如果获取不到 app_name, 则调用原生方法获取
    if(!appName){
      try {
        const nativeInfo = await getNativeInfo()
        appName = normalizeAppName(nativeInfo?.appName)
      } catch (error) {
        console.error('triggerLink error', error)
      }
    }
    
    if (appName === EUFY_HOME_APPLICATION_NAME || appName === EUFY_LIFT_APPLICATION_NAME) {
      console.log('absoluteUrl', absoluteUrl)
      const params = isAndroid ? JSON.stringify({ link: absoluteUrl }) : { link: absoluteUrl }
      await runCommand('openWebview', params)
      return
    }

    // mega 采用 eufysecurity 协议
    if(prefix === 'eufy_mega') {
      prefix = 'eufysecurity'
    }
    // 如果已经有了协议前缀, 则不再添加
    iframe.src =
      absoluteUrl.indexOf(`${prefix}://`) > -1
        ? absoluteUrl
        : `${prefix}://eufy/explore/web#${absoluteUrl}`
    document.body.appendChild(iframe)
    logger.info(`API openWebview called with URL: ${absoluteUrl}`)
    console.log(
      `API openWebview called with URL: ${prefix}://eufy/explore/web#${absoluteUrl}`
    )
    setTimeout(() => {
      document.body.removeChild(iframe)
    })
  })
}

export function openBrowser(url) {
  let absoluteUrl = assembleAbsoluteUrl(url)
  console.log("absoluteUrl", absoluteUrl)
  if (absoluteUrl.indexOf("jump=2") == -1) {
  console.log("11", absoluteUrl)

    if (absoluteUrl.indexOf("?") > -1) {
      absoluteUrl = absoluteUrl.replace(/&?$/, "&jump=2")
    } else {
      absoluteUrl = `${absoluteUrl}?jump=2`
    }
  }

  logger.info(`API openBrowser called with URL: ${absoluteUrl}`)

  if (isIOS) {
    // 判断是在外部浏览器还是app里面打开
    // iframe 形式在外部浏览器打不开 所以直接采用location ，并且兼容之前的版本
    if (window.__IS_APP__) {
      const iframeEle = document.createElement("iframe")
      iframeEle.style.display = "none"
      iframeEle.src = absoluteUrl
      document.body.appendChild(iframeEle)
      setTimeout(() => {
        document.body.removeChild(iframeEle)
      })
    } else {
      window.location.href = absoluteUrl
    }
  } else {
    window.location.href = absoluteUrl
  }
}

export function togglePullToRefresh(isRefreshable) {
  if (isAndroid) {
    runCommand("togglePullToRefresh", isRefreshable)
  }
}

// 获取 baby care 某天的图表数据
export function getBabyHistory() {
  if (isIOS) {
    return new Promise((resolve, reject) => {
      setTimeout(
        reject.bind(null, { message: "Get native info timeout." }),
        TIMEOUT
      )
      setupWebViewJavascriptBridge((bridge) => {
        bridge.callHandler("getDashboardHistoryInfo", {}, (response) => {
          resolve(response)
        })
      })
    })
  }
  if (isAndroid) {
    return Promise.resolve(runCommand("getDashboardHistoryInfo")).then((resp) =>
      JSON.parse(resp)
    )
  }
  return Promise.resolve({})
  // return Promise.resolve(runCommand('getDashboardHistoryInfo')).then(resp => JSON.parse(resp))
}

// 获取 baby care 某天的图表数据
export function babyDashboardHistoryBottomNoButtonClicked(config) {
  console.log("babyDashboardHistoryBottomNoButtonClicked condig: ", config)
  if (isAndroid) {
    return runCommand(
      "babyDashboardHistoryBottomNoButtonClicked",
      JSON.stringify(config)
    )
  }
  return runCommand("babyDashboardHistoryBottomNoButtonClicked", config)
}

export function getRealHeartRateInfo() {
  if (isAndroid) {
    return Promise.resolve(runCommand("getRealHeartRateInfo")).then((resp) => {
      try {
        const data = JSON.parse(resp)
        return data
      } catch (error) {
        console.log(error)
        return null
      }
    })
  } else {
    // ios
    return new Promise((resolve, reject) => {
      setTimeout(
        reject.bind(null, {
          message: "Get baby care dashboard history info timeout.",
        }),
        TIMEOUT
      )
      setupWebViewJavascriptBridge((bridge) => {
        bridge.callHandler("getRealHeartRateInfo", {}, (response) => {
          resolve(response)
        })
      })
    })
  }
}

// 调用 care app 跳转指定faq方法
export function realHeartRateFaqClicked() {
  console.log("call realHeartRateFaqClicked")
  return runCommand("realHeartRateFaqClicked")
}

// 调用 care app 展示指定UI
export function realHeartRateShowErrorUI() {
  return runCommand("realHeartRateShowErrorUI")
}

export function getRealBloodOxygenInfo() {
  if (isAndroid) {
    return Promise.resolve(runCommand("getRealBloodOxygen")).then((resp) => {
      try {
        const data = JSON.parse(resp)
        return data
      } catch (error) {
        console.log(error)
        return null
      }
    })
  } else {
    // ios
    return new Promise((resolve, reject) => {
      setTimeout(
        reject.bind(null, {
          message: "Get baby care blood oxygen info timeout.",
        }),
        TIMEOUT
      )
      setupWebViewJavascriptBridge((bridge) => {
        bridge.callHandler("getRealBloodOxygen", {}, (response) => {
          resolve(response)
        })
      })
    })
  }
}

// 调用 care app 跳转指定faq方法
export function realBloodOxygenFaqClicked() {
  console.log("call real blood oxygen faq clicked")
  return runCommand("realBloodOxygenFaqClicked")
}

// 调用 care app 展示指定UI
export function realBloodOxygenShowErrorUI() {
  console.log("call real blood oxygen err ui showed")
  return runCommand("realBloodOxygenShowErrorUI")
}

/** 加密 */
export async function encrypt(data) {
  logger.info(`API encrypt called.`, data)
  const res = await getNativeInfo()
  const appVersion = res.appVersion.match(/[\d\.]/g).join("")
  if (appVersion >= "4.5.1") {
    if (isAndroid) {
      return runCommand("encrypt", JSON.stringify(data))
        .then((resp) => JSON.parse(resp))
        .then((res) => {
          if (res.code === 0) {
            return res.data
          } else {
            return Promise.reject(`Encrypt Error: ${JSON.parse(res)}`)
          }
        })
    } else if (isIOS) {
      return runCommand("encrypt", data).then((res) => {
        if (res.code === 0) {
          return res.data
        } else {
          return Promise.reject(`Encrypt Error: ${JSON.parse(res)}`)
        }
      })
    }
  } else {
    if (isIOS || isAndroid) {
      return runCommand("aesEncrypt", String(data.data))
    }
  }
  return Promise.reject(`Unknown platform: ${window.navigator.userAgent}`)
}

/** 解密 */
export async function decrypt(data) {
  logger.info(`API decrypt called.`)
  if (!data.data || typeof data.data === "object") {
    return data.data
  }
  const res = await getNativeInfo()
  const appVersion = res.appVersion.match(/[\d\.]/g).join("")
  if (appVersion >= "4.5.1") {
    if (isAndroid) {
      return runCommand("decrypt", JSON.stringify(data))
        .then((resp) => JSON.parse(resp))
        .then((res) => {
          if (res.code === 0) {
            return JSON.parse(res.data)
          } else {
            return Promise.reject(`Decrypt Error: ${JSON.stringify(res)}`)
          }
        })
    } else if (isIOS) {
      return runCommand("decrypt", data).then((res) => {
        if (res.code === 0) {
          return JSON.parse(res.data)
        } else {
          return Promise.reject(`Decrypt Error: ${JSON.stringify(res)}`)
        }
      })
    }
    return Promise.reject(`Unknown platform: ${window.navigator.userAgent}`)
  } else {
    return data.data
  }
}

// v3 app Header 解密
export function getHeaderV3(ts, once, body, domain = '') {
  if (isAndroid) {
    if (domain) {
      return runCommand('getHeaderV3', ts, once, body, domain).then(res => JSON.parse(res))
    }
    return runCommand("getHeaderV3", ts, once, body).then((res) =>
      JSON.parse(res)
    )
  }
  if (isIOS) {
    if (domain) {
      return runCommand('getHeaderV3', JSON.stringify({ ts, once, body, domain }))
    }
    return runCommand("getHeaderV3", JSON.stringify({ ts, once, body }))
  }
  return Promise.reject(`Unknown platform: ${window.navigator.userAgent}`)
}

/** 加密 v3 app */
export async function encryptV3(data) {
  logger.info(`API encryptV3 called.`, data)
  const res = await getNativeInfo()
  const appVersion = res.appVersion.match(/[\d\.]/g).join("")
  if (appVersion >= "4.5.1") {
    if (isAndroid) {
      return runCommand("encryptV3", JSON.stringify(data))
        .then((resp) => JSON.parse(resp))
        .then((res) => {
          if (res.code === 0) {
            return res.data
          } else {
            return Promise.reject(`EncryptV3 Error: ${JSON.parse(res)}`)
          }
        })
    } else if (isIOS) {
      return runCommand("encryptV3", data).then((res) => {
        if (res.code === 0) {
          return res.data
        } else {
          return Promise.reject(`EncryptV3 Error: ${JSON.parse(res)}`)
        }
      })
    }
  } else {
    if (isIOS || isAndroid) {
      return runCommand("aesEncrypt", String(data.data))
    }
  }
  return Promise.reject(`Unknown platform: ${window.navigator.userAgent}`)
}

/**
 * v3 app 解密
 * app方法 decryptV3
 * */
export async function decryptV3App(data) {
  if (!data.data || typeof data.data === "object") {
    return data.data
  }
  const res = await getNativeInfo()
  const appVersion = res.appVersion.match(/[\d\.]/g).join("")
  if (appVersion >= "4.5.1") {
    if (isAndroid) {
      return runCommand("decryptV3", JSON.stringify(data))
        .then((resp) => JSON.parse(resp))
        .then((res) => {
          if (res.code === 0) {
            return JSON.parse(res.data)
          } else {
            return Promise.reject(`decryptV3 Error: ${JSON.stringify(res)}`)
          }
        })
    } else if (isIOS) {
      return runCommand("decryptV3", data).then((res) => {
        if (res.code === 0) {
          return JSON.parse(res.data)
        } else {
          return Promise.reject(`decryptV3 Error: ${JSON.stringify(res)}`)
        }
      })
    }
    return Promise.reject(`Unknown platform: ${window.navigator.userAgent}`)
  } else {
    return data.data
  }
}

// v3 h5解密
export async function decryptV3H5(data, shareKey) {
  if (!data) {
    return data
  }
  const decryptjson = await AesCBCDecryptV3(data, shareKey)
  try {
    return JSON.parse(decryptjson)
  } catch (error) {
    return decryptjson
  }
}

// 调用 care app 跳转指定faq方法
export function exitApp() {
  console.log("call exitApp")
  return runCommand("exitApp")
}

export function readAgreement() {
  console.log("call readAgreement")
  return runCommand("readAgreement")
}

export function startAddDevice() {
  console.log("call startAddDevice")
  return runCommand("startAddDevice")
}

// 跳转RN页面
// plugin_id: string;//插件id
// device_sn: string;//device_sn
// initial_routeName: string;//跳转的页面
// initial_properties: {};//跳转的参数
export function navigateRN(parmas) {
  console.log("navigateRN condig: ", parmas)
  if (isAndroid) {
    return runCommand("navigateRN", JSON.stringify(parmas))
  }
  return runCommand("navigateRN", parmas)
}

export function successfulWarrantySubmissions() {
  return runCommand("successfulWarrantySubmissions")
}

export function uploadTrackingsForClean(params) {
  const { events } = params

  // const date = new Date()
  // const year = date.getFullYear()
  // const months = `0${date.getMonth() + 1}`.substr(-2)
  // const days = `0${date.getDate()}`.substr(-2)
  // const hours = `0${date.getHours()}`.substr(-2)
  // const minutes = `0${date.getMinutes()}`.substr(-2)
  // const seconds = `0${date.getSeconds()}`.substr(-2)

  events.forEach((v) => {
    defaultsDeep(v, {
      mac: "",
      product_code: "",
      time: String(Date.now()),
      // name: "",
      // value_string: "",
      module: "",
      device_id: "",
      sn: "",
      // type: "",
    })
  })
  console.log("uploadTrackingsForClean data: ", params)
  if (isAndroid) {
    // 注意, android 接受 json 字符串参数
    return runCommand("uploadTrackings", JSON.stringify(params))
  }
  return runCommand("uploadTrackings", params)
}

// 调用 clean app 登出的方法
export function appLoginOut() {
  console.log("call appLoginOut")
  return runCommand("appLoginOut")
}
export function showGoToRestrictedZone() {
  console.log("call showGoToRestrictedZone native")
  return runCommand("showGoToRestrictedZone")
}
export function pageAction(action) {
  console.log("call showGoToRestrictedZone native",)
  return runCommand("pageAction",action)
}


